import React, { useMemo } from "react";
import { Field, useFormik } from "formik";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import { useIntl } from "react-intl";
import {
  Button,
  Div,
  InputGroup,
  Select,
  Typography
} from "@icecreamsocial/components";
import { ChonkyInput } from "../ChonkyInput";
import { Wizard } from "../Wizard";
import { PhoneNumberInput } from "./PhoneNumberInput";
import { CountryCodeSelect } from "./CountryCodeSelect";
import { useAtom } from "jotai";
import {
  anyConsentRequiredAtom,
  requiredConsentAtom
} from "../../../shared/atoms";
import { PrivacyStatement } from "../PrivacyStatement";
import { EmailConsentCheckbox } from "./EmailConsentCheckbox";
import { PhoneNumberConsentCheckbox } from "./PhoneNumberConsentCheckbox";

/**
 * @todo turn this into a container component
 */

export const ContactCollection = ({
  unlockLabel = "Activate Offer",
  continueLabel = "Continue",
  useSMS = false,
  privacyURL,
  defaultEmail,
  defaultMobileNumber,
  onSubmit
}) => {
  const [anyConsentRequired] = useAtom(anyConsentRequiredAtom);
  const [requireConsent] = useAtom(requiredConsentAtom);
  const intl = useIntl();

  /**
   * Localized strings
   */
  const OPTIONAL_FIELD = intl.formatMessage({
    id: "contact.hint.optional",
    defaultMessage: "Optional",
    description: "Hint that says the field is optional"
  });

  const EMAIL_FIELD = intl.formatMessage({
    id: "contact.label.email",
    defaultMessage: "Your email address",
    description: "Label for email address collection for consent purposes"
  });

  const PHONE_FIELD = intl.formatMessage({
    id: "contact.label.phone",
    defaultMessage: "Your phone number",
    description: "Label for mobile phone number collection for consent purposes"
  });

  const CONTACT_CONSENT_STATEMENT = intl.formatMessage({
    id: "contact.consent.statement",
    defaultMessage:
      "We'll contact you here with offer and reward updates. Unsubscribe anytime.",
    description:
      "Explanation for contact info data collection. Reassures user that they can unsubscribe later"
  });

  const initialValues = {
    email: defaultEmail,
    mobileNumber: defaultMobileNumber,
    emailConsent: false,
    smsConsent: false
  };

  const inputs = [
    props => (
      <InputGroup {...props}>
        <InputGroup.Label
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {EMAIL_FIELD}{" "}
          {useSMS && <Typography.Span as="i">{OPTIONAL_FIELD}</Typography.Span>}
        </InputGroup.Label>
        <InputGroup.Fields paddingLeft="md">
          <ChonkyInput
            as={Field}
            type="email"
            name="email"
            defaultValue={defaultEmail}
            required={!useSMS}
          />
        </InputGroup.Fields>
      </InputGroup>
    ),
    props => (
      <InputGroup {...props}>
        <InputGroup.Label
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {PHONE_FIELD}{" "}
          {!useSMS && (
            <Typography.Span as="i">{OPTIONAL_FIELD}</Typography.Span>
          )}
        </InputGroup.Label>

        <InputGroup.Fields overflow="visible" alignItems="center">
          <CountryCodeSelect
            name="country"
            defaultCountry={"US"}
            boxShadows="none"
            border="0"
            padding="md"
          />
          <PhoneNumberInput
            as={Field}
            type="tel"
            name="mobileNumber"
            required={useSMS}
            country={props.formik.values.country?.abbreviation}
            defaultValue={defaultMobileNumber}
          />
        </InputGroup.Fields>
        {props.formik?.errors.mobileNumber ? (
          <Typography.Span display="block" textColor="bad" marginTop="sm">
            {props.formik.errors.mobileNumber}
          </Typography.Span>
        ) : null}
      </InputGroup>
    )
  ];

  const checkboxes = [EmailConsentCheckbox, PhoneNumberConsentCheckbox];

  // want the prioritized input to be displayed first
  const orderedInputs = useSMS ? [...inputs].reverse() : [...inputs];

  const orderedConsent = useSMS ? [...checkboxes].reverse() : [...checkboxes];

  return (
    <Wizard
      initialValues={initialValues}
      onSubmit={onSubmit}
      lastStepLabel={unlockLabel}
      continueLabel={continueLabel}
    >
      <Wizard.Step>
        {formik => (
          <>
            {orderedInputs.map((Input, key) => (
              <Input
                key={key}
                formik={formik}
                elWidth="100%"
                marginBottom="md"
              />
            ))}
            <PrivacyStatement privacyURL={privacyURL} />
          </>
        )}
      </Wizard.Step>
      {anyConsentRequired ? (
        <Wizard.Step>
          {(formik, ctx) => (
            <>
              {orderedConsent.map((Consent, key) => (
                <Consent
                  formik={formik}
                  ctx={ctx}
                  key={key}
                  grid
                  gridColumns="auto 10fr auto"
                  alignItems="center"
                  elWidth="100%"
                  marginBottom="md"
                />
              ))}
              <Typography.P typoSize=".8em" marginTop="lg">
                {CONTACT_CONSENT_STATEMENT}
              </Typography.P>
              <PrivacyStatement privacyURL={privacyURL} />
            </>
          )}
        </Wizard.Step>
      ) : null}
    </Wizard>
  );
};
